import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import ChangePasswordDialog from '@/components/common/ChangePasswordDialog';
import LCIcon from '@/components/common/LCIcon';

interface SidebarLayoutProps {
  children: React.ReactNode;
}

const NAV = [
  { id: 'dashboard', label: 'Portfolio Dashboard', icon: 'dashboard', path: '/' },
  { id: 'alerts',    label: 'Margin Alerts',       icon: 'alerts',    path: '/alerts' },
  { id: 'exposure',  label: 'Exposure Analysis',   icon: 'exposure',  path: '/exposure' },
  { id: 'comments',  label: 'All Comments',        icon: 'comments',  path: '/comments' },
  { id: 'admin',     label: 'Admin Panel',         icon: 'admin',     path: '/admin', adminOnly: true },
];

function MarketStatus() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const ny = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const mins = ny.getHours() * 60 + ny.getMinutes();
  const open = mins >= 570 && mins <= 960;
  const time = ny.toLocaleTimeString('en-US', { hour12: false });
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between text-[11px] text-[#7A7A76]">
        <span className="tracking-[0.06em] uppercase font-bold text-[9.5px]">Market</span>
        <span className={`inline-flex items-center gap-[5px] text-[10px] font-bold px-[7px] py-[2px] tracking-[0.08em] uppercase ${open ? 'text-[#1f8a4a] bg-[rgba(31,138,74,0.10)]' : 'text-[#999591] bg-black/[0.06]'}`}>
          <span className="w-1.5 h-1.5 rounded-full bg-current" />{open ? 'Open' : 'Closed'}
        </span>
      </div>
      <div className="flex items-center justify-between text-[11px] text-[#7A7A76]">
        <span className="tracking-[0.06em] uppercase font-bold text-[9.5px]">EST</span>
        <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11, color: '#333' }}>{time}</span>
      </div>
    </div>
  );
}

const SidebarLayout: React.FC<SidebarLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [privacyMode, setPrivacyMode] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const canAccessAdmin = user?.role === 'admin' || user?.role === 'super';

  useEffect(() => {
    document.body.dataset['privacy'] = privacyMode ? 'on' : 'off';
  }, [privacyMode]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const getUserDisplayName = () => {
    if (user?.first_name && user?.last_name) return `${user.first_name} ${user.last_name}`;
    if (user?.email) return user.email.split('@')[0];
    return 'User';
  };

  const getUserRole = () => {
    if (!user?.role) return 'User';
    return user.role.charAt(0).toUpperCase() + user.role.slice(1);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="grid min-h-screen max-[768px]:grid-cols-1 [grid-template-columns:224px_1fr]">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/[0.36] z-[199]"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`bg-white border-r border-black/[0.08] flex flex-col sticky top-0 h-screen max-[768px]:fixed max-[768px]:left-0 max-[768px]:top-0 max-[768px]:h-screen max-[768px]:w-[224px] max-[768px]:z-[200] max-[768px]:transition-transform max-[768px]:duration-[220ms] ${sidebarOpen ? 'max-[768px]:translate-x-0' : 'max-[768px]:-translate-x-full'}`}>
        {/* Brand */}
        <div className="flex items-center gap-2.5 px-[18px] pt-[18px] pb-[22px] border-b border-black/[0.06] shrink-0">
          <div className="w-9 h-9 bg-[#E50025] grid place-items-center text-white font-serif font-[800] text-[18px] tracking-[-0.02em] shrink-0">M</div>
          <div className="h-9 flex flex-col leading-none justify-between">
            <span className="text-[10px] font-bold tracking-[0.14em] uppercase text-[#E50025]">Lighthouse Canton</span>
            <span className="text-[15px] font-medium font-serif text-black mt-1 tracking-[-0.005em]">Margin Risk System</span>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex flex-col gap-px px-2.5 py-3.5 flex-1 overflow-y-auto">
          {NAV.filter(item => !(item as any).adminOnly || canAccessAdmin).map(item => {
            const active = isActive(item.path) || (item.path === '/' && location.pathname.startsWith('/portfolio'));
            return (
              <button
                key={item.id}
                className={`flex items-center gap-3 px-3 py-[9px] text-[13.5px] cursor-pointer border-0 bg-transparent text-left w-full rounded-sm relative transition-colors duration-[120ms] ${active ? 'text-[#E50025] font-semibold bg-[#FAF9F8]' : 'font-normal text-[#333] hover:bg-[#FAF9F8] hover:text-black'}`}
                onClick={() => navigate(item.path)}
              >
                {active && <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 bg-[#E50025]" />}
                <LCIcon name={item.icon} size={17} className={`shrink-0 ${active ? 'opacity-100' : 'opacity-85'}`} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="border-t border-black/[0.06] px-3.5 py-3 pb-4 flex flex-col gap-3 shrink-0">
          <div className="flex items-center justify-between py-1">
            <span className="flex items-center gap-2 text-[12.5px] text-[#333]">
              <LCIcon name="eye" size={14} />
              Privacy Mode
            </span>
            <span
              className={`lc-switch${privacyMode ? ' on' : ''}`}
              onClick={() => setPrivacyMode(v => !v)}
              role="switch"
              aria-checked={privacyMode}
            />
          </div>

          <MarketStatus />
        </div>

          <div className="flex flex-col py-1 border-t border-black/[0.06] px-3.5 py-3 pb-4 flex flex-col gap-2 shrink-0">
            <div className="text-[13px] font-bold text-black tracking-[-0.005em]">{getUserDisplayName()}</div>
            <div className="flex justify-between mt-2 text-[11.5px] font-normal text-[#7A7A76]">
              <span className="tracking-[0.06em] uppercase font-bold text-[9.5px]">
                {getUserRole()}
              </span>
              <a href="#" className="inline-flex items-center text-inherit hover:text-[#E50025] hover:no-underline" onClick={e => { e.preventDefault(); handleLogout(); }}>
                <LCIcon name="logout" size={12} /> Logout
              </a>
              {/* <a href="#" className="inline-flex items-center gap-1 text-inherit hover:text-[#E50025] hover:no-underline" onClick={e => { e.preventDefault(); setChangePasswordOpen(true); }}>
                <LCIcon name="key" size={12} /> Password
              </a> */}
            </div>
          </div>
      </div>

      {/* Main */}
      <main className="flex flex-col min-h-screen min-w-0 overflow-x-hidden">
        {/* Mobile top bar */}
        <div className="hidden max-[768px]:flex items-center gap-3 px-4 py-2.5 bg-white border-b border-black/[0.08] sticky top-0 z-[50] shrink-0">
          <button
            className="bg-transparent border-0 cursor-pointer p-1.5 text-black flex items-center justify-center shrink-0 hover:text-[#E50025]"
            onClick={() => setSidebarOpen(v => !v)}
            aria-label="Toggle menu"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
          <div className="w-7 h-7 bg-[#E50025] grid place-items-center text-white font-sans font-[800] text-[14px] shrink-0">L</div>
          <span className="text-[13px] font-semibold text-black flex-1 tracking-[-0.01em]">Lighthouse Canton</span>
        </div>
        {children}
      </main>

      {/* <ChangePasswordDialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      /> */}
    </div>
  );
};

export default SidebarLayout;
